import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Hidden from '@material-ui/core/Hidden';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import Skeleton from '@material-ui/lab/Skeleton';

// import pdcLogoDark from '../icons/pdc_identicard_dark.svg';
// import pdcLogo from '../icons/pdc_identicard.svg';
import CameraCapture from './CameraCapture.jsx';

const useStyles = makeStyles((theme) => ({
  skeletonDevice: {
    position: 'absolute',
    right: '2%',
  },
  menuButton: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.8),
    marginBottom: theme.spacing(0.8),
    color: 'gray',
  },
  logo: {
    maxWidth: theme.spacing(15),
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1.2),
    paddingLeft: 0,
  },
  contentMobile: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    flexWrap: 'nowrap',
    alignContent: 'flex-start',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  avatar: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(-0.5),
    width: '100%',
    height: '100%',
    alignSelf: 'center',
  },
  skeletonAvatar: {
    margin: theme.spacing(1),
    width: '100%',
    alignSelf: 'center',
    height: '50%',
    marginLeft: '30%',
    marginTop: '10%',
  },
  fullWidth: {
    width: '96%',
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  avatarMobile: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  oneThird: {
    width: '33%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  twoThirds: {
    width: '77%', // TODO: 33 + 77 != 100
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  skeletonTwoThird: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    marginLeft: '5%',
    width: '58%',
    marginRight: '16%',
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  hidden: {
    display: 'none',
  },
  leftContent: {
    justifyContent: 'left',
  },
  fullWidthWithMargin: {
    width: '100%',
  },
  withMargins: {
    margin: theme.spacing(1),
    width: (isMobile) => (isMobile ? '75vw' : '100%'),
  },
  centered: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  photoIcon: {
    fontSize: theme.spacing(5),
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(2),
    color: '#FFFFFF',
  },
  textBoxcombo: {
    margin: theme.spacing(1),
    width: (isMobile) => (isMobile ? '75vw' : '96%'),
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
}));

const CardholderAddEdit = ({
  loading,
  cardholder,
  setCardholder,
  dataEntryFields,
  contactFields,
  isMobile,
  isNewRecord,
}) => {
  const classes = useStyles(isMobile);
  const theme = useTheme();
  const [showContactFields, setShowContactFields] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const toggleShowContactFields = () => {
    setShowContactFields(!showContactFields);
  };

  const createTextField = (field) => {
    let fieldIsErrored = false;
    if ((field.id === 'firstName' || field.id === 'lastName') && !field.value) {
      fieldIsErrored = true;
    }
    if (field.type === 'mapping') {
      return (
        <FormControl variant="outlined" key={field.id} className={classes.textBoxcombo}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            native
            label={field.label}
            value={field.value || ''}
            onChange={field.changeHandler}
          >
            <option aria-label="None" value="" />
            {field.mapOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.data}
              </option>
            ))}
          </Select>
        </FormControl>
      );
    }
    return (
      <FormControl key={field.id} className={classes.fullWidth}>
        <TextField
          className={classes.withMargins}
          onChange={field.changeHandler}
          value={field.value}
          label={field.label}
          variant="outlined"
          required={field.required}
          error={fieldIsErrored}
        />
      </FormControl>
    );
  };
  const generateFields = (rowsList) => {
    const sortedRowsList = rowsList.sort((a, b) => (a.weight > b.weight ? 1 : -1));
    return sortedRowsList.map((row) => {
      if (row.fields.length === 1) {
        if (row.fields[0].mapOptions != null) {
          return (
            <div key={`row:${row.weight}`} className={classes.fieldRow}>
              {createTextField(row.fields[0])}
            </div>
          );
        }
      }
      const sortedFields = row.fields.sort((a, b) => (a.weight > b.weight ? 1 : -1));
      const fieldComponents = sortedFields.map((field) => {
        return createTextField(field);
      });
      return (
        <div key={`row:${row.weight}`} className={classes.fieldRow}>
          {fieldComponents}
        </div>
      );
    });
  };

  let dataEntryFieldRenderings = [];
  let contactFieldRenderings = [];

  if (dataEntryFields && dataEntryFields.length > 0) {
    dataEntryFieldRenderings = generateFields(dataEntryFields);
  }

  if (contactFields && contactFields.length > 0) {
    contactFieldRenderings = generateFields(contactFields);
  }

  const handleClick = () => {
    setShowImageModal(true);
  };

  const setPhoto = (photo) => {
    setCardholder({ ...cardholder, photo });
    setShowImageModal(false);
  };

  return dataEntryFieldRenderings.length ? (
    <>
      {!isMobile ? (
        <div className={classes.blueMenu}>
          <Typography className={classes.menuButton}>Cardholder Information</Typography>
        </div>
      ) : (
        <div />
      )}
      <div className={isMobile ? classes.contentMobile : classes.content}>
        <div className={isMobile ? classes.avatarMobile : classes.oneThird}>
          <Button onClick={handleClick}>
            <Avatar
              variant="square"
              alt={`${cardholder.firstName} ${cardholder.lastName}`}
              src={cardholder.photo}
              className={classes.avatar}
            />
            <div className={[classes.avatar, classes.overlay]}>
              <AddAPhotoIcon size="large" className={classes.photoIcon} />
            </div>
          </Button>
          <Hidden smDown implementation="css" className={classes.centered}>
            {/* <img
              className={classes.logo}
              src={theme.palette.type === 'dark' ? pdcLogoDark : pdcLogo}
              alt="PDC IDenticard"
            /> */}
          </Hidden>
        </div>
        <div className={!isMobile ? classes.twoThirds : {}}>
          {dataEntryFieldRenderings}
          {contactFields && contactFields.length > 0 && (
            <div className={(classes.fieldRow, classes.withMargins)}>
              <Button
                variant="outlined"
                onClick={() => {
                  toggleShowContactFields();
                }}
                className={[classes.fullWidthWithMargin, classes.leftContent]}
              >
                Personal Contact Information
                {!showContactFields ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
              </Button>
            </div>
          )}

          <div className={!showContactFields ? classes.hidden : ''}>{contactFieldRenderings}</div>
        </div>
        <CameraCapture
          visible={showImageModal}
          acceptPhoto={setPhoto}
          onCancel={() => setShowImageModal(false)}
        />
      </div>
    </>
  ) : (
    <>
      <div className={classes.blueMenu}>
        <Typography className={classes.menuButton}>
          <Skeleton variant="text" width={theme.spacing(26.875)} />
        </Typography>
      </div>
      <div className={classes.content}>
        <div style={{ width: '50%', display: 'flex' }}>
          <div className={classes.oneThird}>
            <Avatar variant="square" className={classes.skeletonAvatar}>
              <Skeleton variant="rect" />
            </Avatar>
          </div>
          <div className={classes.skeletonTwoThird}>
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
            <Skeleton width={theme.spacing(55)} height={theme.spacing(8)} />
          </div>
        </div>
        <div
          style={{ width: '50%', paddingLeft: '4%' }}
          className={isNewRecord ? classes.skeletonDevice : null}
        >
          <Skeleton
            width={theme.spacing(93.75)}
            height={theme.spacing(5.25)}
            style={{ marginTop: '-52px', marginBottom: '30px' }}
          />
          <Skeleton
            width={theme.spacing(70)}
            height={theme.spacing(4)}
            style={{ marginTop: '8px' }}
          />
          <Skeleton
            width={theme.spacing(70)}
            height={theme.spacing(4)}
            style={{ marginTop: '8px' }}
          />
          <Skeleton
            width={theme.spacing(70)}
            height={theme.spacing(4)}
            style={{ marginTop: '8px' }}
          />
          <Skeleton
            width={theme.spacing(70)}
            height={theme.spacing(4)}
            style={{ marginTop: '8px' }}
          />
        </div>
      </div>
    </>
  );
};

export default CardholderAddEdit;
